import { useEffect, useState, useRef } from 'react'
import { Image } from 'src/components/ui/Image'
import Icon from 'src/components/ui/Icon'

import { GalleryMovie } from '../GalleryMovie'
import type { Image as TImage, Video } from '../types/gallery-image'
import { GalleryFullView } from '../GalleryFullView'

type FullScreenGalleryProps = {
  isOpen: boolean
  onClose: () => void
  images: Array<TImage | Video>
  initialIndex?: number
}

const getImageUrl = (item: TImage | Video): string => {
  return 'imageUrl' in item ? item.imageUrl : item.url
}

const getImageText = (item: TImage | Video): string => {
  return 'imageText' in item ? item.imageText : ''
}

const isVideo = (item: TImage | Video): item is Video => {
  return 'url' in item
}

export const FullScreenGallery = ({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
}: FullScreenGalleryProps) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(initialIndex)
  const [showGalleryFullView, setShowGalleryFullView] = useState(false)
  const parentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'auto'
    }
  }, [isOpen])

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index)
    setShowGalleryFullView(true)
  }

  const handleCloseGalleryFullView = () => {
    setShowGalleryFullView(false)
  }

  if (!isOpen) {
    return null
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-white z-[88888] overflow-auto"
        data-testid="full-screen-gallery"
      >
        <header className="sticky top-0 flex items-center px-4 py-3 bg-white z-[1]">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full absolute left-2"
            data-testid="full-screen-gallery-close"
          >
            <Icon
              name="NewChevronLeft"
              width={8}
              height={14}
              className="text-restructure-primary"
            />
          </button>
          <h2 className="text-base font-medium w-full text-center">
            Galeria de Imagens
          </h2>
        </header>

        <div ref={parentRef} className="grid gap-2 pb-2">
          {Array.from({ length: Math.ceil(images.length / 3) }).map(
            (_, groupIndex) => {
              const startIndex = groupIndex * 3
              const groupImages = images.slice(startIndex, startIndex + 3)

              return (
                <>
                  {groupImages[0] && (
                    <div key={`large-${startIndex}`} className="w-full">
                      <div className="aspect-square w-full overflow-hidden">
                        <button
                          onClick={() => handleImageClick(startIndex)}
                          className="w-full h-full"
                        >
                          {isVideo(groupImages[0]) ? (
                            <GalleryMovie {...groupImages[0]} />
                          ) : (
                            <Image
                              src={getImageUrl(groupImages[0])}
                              alt={getImageText(groupImages[0])}
                              width={628}
                              height={628}
                              loading={startIndex < 4 ? 'eager' : 'lazy'}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  {(groupImages[1] || groupImages[2]) && (
                    <div
                      key={`small-${startIndex}`}
                      className="grid grid-cols-2 gap-2"
                    >
                      {groupImages[1] && (
                        <div className="aspect-square w-full overflow-hidden">
                          <button
                            onClick={() => handleImageClick(startIndex + 1)}
                            className="w-full h-full"
                          >
                            {isVideo(groupImages[1]) ? (
                              <GalleryMovie {...groupImages[1]} />
                            ) : (
                              <Image
                                src={getImageUrl(groupImages[1])}
                                alt={getImageText(groupImages[1])}
                                width={628}
                                height={628}
                                loading={startIndex + 1 < 4 ? 'eager' : 'lazy'}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </button>
                        </div>
                      )}
                      {groupImages[2] && (
                        <div className="aspect-square w-full overflow-hidden">
                          <button
                            onClick={() => handleImageClick(startIndex + 2)}
                            className="w-full h-full"
                          >
                            {isVideo(groupImages[2]) ? (
                              <GalleryMovie {...groupImages[2]} />
                            ) : (
                              <Image
                                src={getImageUrl(groupImages[2])}
                                alt={getImageText(groupImages[2])}
                                width={628}
                                height={628}
                                loading={startIndex + 2 < 4 ? 'eager' : 'lazy'}
                                className="w-full h-full object-cover"
                              />
                            )}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )
            }
          )}
        </div>
      </div>

      {showGalleryFullView && (
        <GalleryFullView
          isOpen={showGalleryFullView}
          closeGalleryView={handleCloseGalleryFullView}
          images={images}
          selectedImageForClick={selectedImageIndex}
        />
      )}
    </>
  )
}
